<template>
  <div class="pdf-field-editor" v-if="hasFile">
    <div class="pdf-field-editor__toolbar">
      <PdfViewportTools
        v-if="pages && loaded"
        :pages="pages"
        v-model="page"
      />
      <span v-if="loaded" class="pdf-field-editor__count">
        {{ fieldsOnPage.length }} en esta página · {{ value.length }} en total
      </span>
      <div class="pdf-field-editor__zoom" v-if="loaded">
        <b-button size="sm" variant="outline-primary" @click="zoomOut">
          <feather-icon icon="ZoomOutIcon" />
        </b-button>
        <span class="pdf-field-editor__zoom-label">{{ zoomPercent }}%</span>
        <b-button size="sm" variant="outline-primary" @click="zoomIn">
          <feather-icon icon="ZoomInIcon" />
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="resetZoom">
          100%
        </b-button>
      </div>
    </div>

    <div ref="scroll" class="pdf-field-editor__scroll">
      <!-- `zoomFrame` reserva el espacio del documento ya escalado para que el
           scroll funcione; `page` aplica el zoom como transform visual, de modo
           que el canvas (fijado a px scale-1 por vue-pdf-signature) y el overlay
           escalen solidariamente sin drift. -->
      <div class="pdf-field-editor__zoom-frame" :style="zoomFrameStyle">
        <div
          ref="page"
          class="pdf-field-editor__page"
          :style="pageStyle"
          @click="onPageClick"
        >
          <Pdf
            class="pdf-field-editor__canvas"
            :page="actualPage"
            :src="file"
            ref="pdf"
            @page-loaded="onPageLoaded"
            @page-size="onPageSize"
          />
          <div ref="overlay" class="pdf-field-editor__overlay">
            <DraggableField
              v-for="field in fieldsOnPage"
              :key="field.id"
              ref="fieldRefs"
              :data-field-id="field.id"
              :field="field"
              :screen="screenRectFor(field)"
              :bounds="overlayBounds"
              :container="overlayEl"
              :zoom="zoom"
              :snap-lines-provider="snapLinesFor"
              :selected="field.id === selectedId"
              :closable="!readonly"
              @select="selectField(field.id)"
              @delete="removeField(field.id)"
              @geometry-change="onGeometryChange(field, $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <b-overlay :show="!loaded" no-wrap />
  </div>
</template>

<script>

import Pdf from 'vue-pdf-signature';

import PdfViewportTools from '@/febos/global/pdf/components/PdfViewportTools';
import DraggableField from '@/febos/global/pdf/formBuilder/components/DraggableField';
import PdfCoordinateService from '@/febos/global/pdf/formBuilder/services/PdfCoordinateService';
import { v4 as uuidv4 } from 'uuid';
import { ioDownloadPrivateFile } from '@/febos/servicios/api/herramientas.api';
import {
  createField,
  defaultSizeForType,
  uniqueFieldName,
  DATA_FIELD_TYPES,
} from '@/febos/global/pdf/formBuilder/model/fieldModel';

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;

/**
 * PdfFieldEditor — visor PDF.js (vía vue-pdf-signature) con una capa de campos
 * posicionables en coordenadas PDF (puntos, origen abajo-izquierda).
 *
 * NO toca PdfViewport.vue (flujo de firmas en producción). Reutiliza el
 * mismo pipeline de carga desde S3 y los page tools.
 *
 * Modelo de datos (`value` / v-model): array de PdfField (ver fieldModel.js),
 * con x/y/width/height en PUNTOS PDF. El editor convierte a px solo para pintar.
 */
export default {
  name: 'PdfFieldEditor',
  components: {
    Pdf,
    PdfViewportTools,
    DraggableField,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: { type: Array, default: () => [] },
    s3url: { type: String, default: undefined },
    path: { type: String, default: undefined },
    name: { type: String, default: 'documento.pdf' },
    activeFieldType: { type: String, default: null },
    // Tipos de campo permitidos en este editor. Por defecto solo campos de
    // datos (sin firma/iniciales, que las administra el módulo de firma).
    allowedFieldTypes: { type: Array, default: () => [...DATA_FIELD_TYPES] },
    // Campo seleccionado, sincronizable con `.sync` para compartir selección
    // con un panel/listado externo.
    selectedId: { type: String, default: null },
    readonly: { type: Boolean, default: false },
    defaultPage: { type: Number, default: 1 },
    // Si el editor está activo/visible. Cuando false, ignora atajos de teclado
    // (la demo mantiene el editor montado con v-show incluso en modo completar).
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      file: undefined,
      pdfDocument: undefined,
      pages: undefined,
      page: this.defaultPage,
      loaded: false,
      publicandoFile: false,
      zoom: 1,
      // Dimensiones por página (puntos PDF) descubiertas vía page-size.
      pageSizes: {},
      coord: new PdfCoordinateService({ pageWidthPt: 0, pageHeightPt: 0 }),
      overlayBounds: undefined,
      overlayEl: null,
    };
  },
  computed: {
    hasFile() {
      return !!(this.s3url || this.path);
    },
    actualPage() {
      return this.page === -1 ? this.pages : this.page;
    },
    zoomPercent() {
      return Math.round(this.zoom * 100);
    },
    currentPageSize() {
      return this.pageSizes[this.actualPage];
    },
    // El contenedor `page` mantiene el tamaño base (puntos PDF == px a scale 1);
    // el zoom se aplica como transform. Esto evita luchar contra el width/height
    // inline que vue-pdf-signature fija en el canvas.
    pageStyle() {
      const size = this.currentPageSize;
      if (!size) {
        return {};
      }
      const style = {
        width: `${size.width}px`,
        height: `${size.height}px`,
      };
      // IMPORTANTE: un ancestro con `transform` rompe el posicionamiento del
      // control-box de vue-moveable (usa getBoundingClientRect, que incluye el
      // scale, pero su matemática de offset no lo compensa). Por eso NO se
      // aplica transform cuando zoom===1 (caso normal, handles perfectos).
      // Con zoom != 1 se asume el leve desfase hasta migrar el zoom a escala
      // real del canvas.
      if (this.zoom !== 1) {
        style.transform = `scale(${this.zoom})`;
        style.transformOrigin = 'top left';
      }
      return style;
    },
    // El frame reserva el espacio real (escalado) para que el scroll funcione,
    // porque un transform no afecta el flujo del layout.
    zoomFrameStyle() {
      const size = this.currentPageSize;
      if (!size) {
        return {};
      }
      return {
        width: `${size.width * this.zoom}px`,
        height: `${size.height * this.zoom}px`,
      };
    },
    fieldsOnPage() {
      return this.value.filter((f) => f.page === this.actualPage);
    },
  },
  watch: {
    s3url() { this.loadFile(); },
    path() { this.loadFile(); },
    actualPage() { this.syncCoordForPage(); },
    zoom() { this.$nextTick(this.boundsRefresher); },
    selectedId(id) {
      // Si el campo seleccionado (p.ej. desde el listado) está en otra página,
      // saltar a ella para que sea visible.
      if (!id) {
        return;
      }
      const field = this.value.find((f) => f.id === id);
      if (field && field.page !== this.actualPage) {
        this.page = field.page;
      }
    },
  },
  methods: {
    // --- Carga del PDF (mismo patrón que PdfViewport) ------------------------
    async getS3Url() {
      if (this.s3url) {
        return this.s3url;
      }
      if (!this.path) {
        return '';
      }
      if (this.path.startsWith('http')) {
        return this.path;
      }
      this.publicandoFile = true;
      const { data: { url } } = await ioDownloadPrivateFile({
        path: this.path,
        nombre: this.name,
        descargable: false,
      });
      this.publicandoFile = false;
      return url;
    },
    async loadFile() {
      this.clear();
      const url = await this.getS3Url();
      if (!url) {
        return;
      }
      this.file = Pdf.createLoadingTask(url);
      const loaded = await this.file.promise;
      this.pdfDocument = loaded;
      this.pages = loaded.numPages;
    },
    clear() {
      this.file = undefined;
      this.pdfDocument = undefined;
      this.pages = undefined;
      this.page = this.defaultPage;
      this.loaded = false;
      this.setSelected(null);
      this.pageSizes = {};
    },

    // --- Eventos de vue-pdf-signature ----------------------------------------
    onPageLoaded() {
      if (!this.loaded) {
        this.loaded = true;
        this.$emit('loaded');
      }
      this.$nextTick(() => {
        // El overlay es el `container` de vue-moveable: monta el control-box
        // dentro del overlay (relativo), no en document.body. Esto corrige el
        // desfase de los handles y permite que el CSS local los estilice.
        this.overlayEl = this.$refs.overlay || null;
        this.boundsRefresher();
      });
    },
    onPageSize(widthPt, heightPt) {
      // Dimensiones de la página en puntos PDF (scale 1).
      this.$set(this.pageSizes, this.actualPage, { width: widthPt, height: heightPt });
      this.syncCoordForPage();
      this.$nextTick(this.boundsRefresher);
    },

    // --- Coordenadas ----------------------------------------------------------
    // El servicio opera SIEMPRE a escala base (1 px CSS == 1 punto PDF, con flip
    // de Y). El zoom es un transform visual y no entra en la conversión, por lo
    // que las coordenadas PDF almacenadas son invariantes al zoom (sin drift).
    syncCoordForPage() {
      const size = this.currentPageSize;
      if (!size) {
        return;
      }
      this.coord.setPageSize(size.width, size.height);
      this.coord.setRenderedWidth(size.width);
      this.$nextTick(this.updateOverlayBounds);
    },
    updateOverlayBounds() {
      const el = this.$refs.page;
      if (!el) {
        return;
      }
      // vue-moveable interpreta `bounds` en coordenadas de PANTALLA (viewport),
      // no relativas al contenedor. Por eso se toman del rect absoluto del
      // elemento de página (igual que el original PdfViewport.setBounds). Si se
      // usaran coords relativas (0,0), el drawer lateral desplazaría los límites.
      // Con `container` = overlay, vue-moveable interpreta los bounds RELATIVOS
      // al container, no en coordenadas de pantalla. Por eso el origen es (0,0)
      // y el tamaño es el del overlay. (moveable 0.25 usa {left,top,width,
      // height}; NO usa right/bottom.)
      this.overlayBounds = {
        left: 0,
        top: 0,
        width: el.clientWidth,
        height: el.clientHeight,
      };
    },
    refreshFieldRects() {
      const refs = this.$refs.fieldRefs;
      if (!refs) {
        return;
      }
      const list = Array.isArray(refs) ? refs : [refs];
      list.forEach((cmp) => {
        if (cmp && typeof cmp.refreshRect === 'function') {
          cmp.refreshRect();
        }
      });
    },
    screenRectFor(field) {
      return this.coord.pdfRectToScreen({
        x: field.x, y: field.y, width: field.width, height: field.height,
      });
    },
    // Elementos DOM de los OTROS campos de la página (para guías de alineación).
    // Se resuelven desde el overlay por data-field-id, excluyendo el propio.
    // Líneas de snap (px espacio base) para un campo: bordes izq/centro/der y
    // arriba/medio/abajo de los OTROS campos de la página + bordes y centro de
    // la página. Números (no nodos DOM) -> seguros como prop.
    snapLinesFor(id) {
      const vertical = [];
      const horizontal = [];
      const size = this.currentPageSize;
      if (size) {
        vertical.push(0, size.width / 2, size.width);
        horizontal.push(0, size.height / 2, size.height);
      }
      this.fieldsOnPage.forEach((f) => {
        if (f.id === id) {
          return;
        }
        const r = this.screenRectFor(f);
        vertical.push(r.x, r.x + r.width / 2, r.x + r.width);
        horizontal.push(r.y, r.y + r.height / 2, r.y + r.height);
      });
      return { vertical, horizontal };
    },

    // --- Interacción ----------------------------------------------------------
    onPageClick(event) {
      if (this.readonly) {
        return;
      }
      // Ignorar clicks que vienen de un campo existente (burbuja controlada).
      if (event.target.closest('.draggable-field')) {
        return;
      }
      // Sin tipo activo (o tipo no permitido), un click sobre el PDF solo
      // deselecciona; no crea campo. Permite el modo "sello único".
      if (!this.activeFieldType || !this.allowedFieldTypes.includes(this.activeFieldType)) {
        this.setSelected(null);
        return;
      }
      const size = this.currentPageSize;
      if (!size) {
        return;
      }
      const pageRect = this.$refs.page.getBoundingClientRect();
      // getBoundingClientRect ya refleja el zoom (el transform afecta al rect);
      // dividir por zoom lleva el punto al espacio base del overlay.
      const screenX = (event.clientX - pageRect.left) / this.zoom;
      const screenY = (event.clientY - pageRect.top) / this.zoom;

      const sizePt = defaultSizeForType(this.activeFieldType);
      // En espacio base, 1 px == 1 punto PDF. Centrar el campo en el click.
      const screenRect = {
        x: screenX - sizePt.width / 2,
        y: screenY - sizePt.height / 2,
        width: sizePt.width,
        height: sizePt.height,
      };
      const pdfRect = this.coord.clampPdfRect(this.coord.screenRectToPdf(screenRect));

      const field = createField({
        type: this.activeFieldType,
        page: this.actualPage,
        x: pdfRect.x,
        y: pdfRect.y,
        width: pdfRect.width,
        height: pdfRect.height,
        existing: this.value,
      });
      this.emitFields([...this.value, field]);
      this.setSelected(field.id);
      this.$emit('field-created', field);
    },
    onGeometryChange(field, screenRect) {
      const pdfRect = this.coord.clampPdfRect(this.coord.screenRectToPdf(screenRect));
      const updated = this.value.map((f) => (
        f.id === field.id
          ? { ...f, x: pdfRect.x, y: pdfRect.y, width: pdfRect.width, height: pdfRect.height }
          : f
      ));
      this.emitFields(updated);
    },
    setSelected(id) {
      // selectedId es prop sincronizable (.sync); no se muta directamente.
      this.$emit('update:selectedId', id || null);
    },
    selectField(id) {
      this.setSelected(id);
      this.$emit('field-selected', this.value.find((f) => f.id === id));
    },
    removeField(id) {
      this.emitFields(this.value.filter((f) => f.id !== id));
      if (this.selectedId === id) {
        this.setSelected(null);
      }
      this.$emit('field-deleted', id);
    },
    emitFields(fields) {
      this.$emit('input', fields);
    },
    onKeyDown(e) {
      if (this.readonly || !this.active) {
        return;
      }
      // Escape: deseleccionar siempre.
      if (e.key === 'Escape') {
        this.setSelected(null);
        return;
      }
      // El resto de atajos requieren un campo seleccionado y que el foco no esté
      // en un control de formulario (para no interferir al editar label/opciones).
      if (!this.selectedId || isEditableTarget(e.target)) {
        return;
      }

      // Delete/Backspace: borrar el campo seleccionado.
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        this.removeField(this.selectedId);
        return;
      }

      // Ctrl/Cmd + D: duplicar el campo seleccionado.
      if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        this.duplicateField(this.selectedId);
      }
    },

    // Duplica un campo con un pequeño offset, nuevo id y nombre único. Preserva
    // tipo, estilo, opciones, etc. El nuevo campo queda seleccionado.
    duplicateField(id) {
      const original = this.value.find((f) => f.id === id);
      if (!original) {
        return;
      }
      const OFFSET = 10; // puntos PDF
      const pos = this.coord.clampPdfRect({
        x: original.x + OFFSET,
        y: original.y - OFFSET,
        width: original.width,
        height: original.height,
      });
      const copy = {
        ...JSON.parse(JSON.stringify(original)),
        id: uuidv4(),
        name: uniqueFieldName(original.name, this.value),
        x: pos.x,
        y: pos.y,
      };
      this.emitFields([...this.value, copy]);
      this.setSelected(copy.id);
      this.$emit('field-created', copy);
    },

    // --- Zoom -----------------------------------------------------------------
    zoomIn() {
      this.zoom = Math.min(ZOOM_MAX, +(this.zoom + ZOOM_STEP).toFixed(2));
    },
    zoomOut() {
      this.zoom = Math.max(ZOOM_MIN, +(this.zoom - ZOOM_STEP).toFixed(2));
    },
    resetZoom() {
      this.zoom = 1;
    },
  },
  created() {
    this.loadFile();
    // Handler estable para poder removerlo en beforeDestroy. Refresca los
    // bounds (en coords de pantalla) y reubica los control-box de vue-moveable.
    this.boundsRefresher = () => {
      // En scroll, el reposicionamiento del DOM ocurre antes del paint; correr
      // updateRect dentro de rAF garantiza que vue-moveable lea el rect ya
      // actualizado del target y recoloque sus handles sin desfase.
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
      this.rafId = requestAnimationFrame(() => {
        this.updateOverlayBounds();
        this.refreshFieldRects();
      });
    };
  },
  mounted() {
    // Los bounds y los control-box de vue-moveable viven en coordenadas de
    // pantalla, así que deben refrescarse ante CUALQUIER scroll (el contenedor
    // real que desplaza puede ser este panel, el layout o el window). Se
    // escucha en captura a nivel de documento para captar scroll de cualquier
    // contenedor anidado, más resize de ventana.
    window.addEventListener('resize', this.boundsRefresher);
    document.addEventListener('scroll', this.boundsRefresher, { capture: true, passive: true });
    window.addEventListener('keydown', this.onKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.boundsRefresher);
    document.removeEventListener('scroll', this.boundsRefresher, { capture: true });
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  },
};

/** ¿El evento viene de un campo editable (input/textarea/select/contentEditable)? */
function isEditableTarget(target) {
  if (!target) {
    return false;
  }
  const tag = (target.tagName || '').toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

</script>

<style lang="scss" scoped>

@import '~@core/scss/base/bootstrap-extended/_include';

.pdf-field-editor {
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: $gray-100;
  position: relative;
  overflow: hidden;
}

.pdf-field-editor__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: map-get($spacers, 1);
  gap: map-get($spacers, 1);
}

.pdf-field-editor__zoom {
  display: flex;
  align-items: center;
  gap: map-get($spacers, 1);
}

.pdf-field-editor__zoom-label {
  min-width: 42px;
  text-align: center;
  font-size: 0.85rem;
}

.pdf-field-editor__count {
  font-size: 0.78rem;
  color: #6b6b85;
  white-space: nowrap;
}

.pdf-field-editor__scroll {
  flex: 1;
  overflow: auto;
  padding: map-get($spacers, 1);
}

.pdf-field-editor__page {
  position: relative;
  margin: 0 auto;
  /* el canvas interno se escala vía width/height del contenedor */
}

.pdf-field-editor__canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.pdf-field-editor__overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.pdf-field-editor__snapline {
  position: absolute;
  background: #ff3b6b;
  z-index: 5;
  pointer-events: none;
}

.pdf-field-editor__snapline--v {
  top: 0;
  bottom: 0;
  width: 1px;
}

.pdf-field-editor__snapline--h {
  left: 0;
  right: 0;
  height: 1px;
}

</style>

<!--
  Estilos NO scoped: vue-moveable monta su control-box dentro del `container`
  (el overlay), con clases globales. Se anclan bajo `.pdf-field-editor__overlay`
  para dar color/visibilidad a los handles SIN afectar al componente de firmas
  (que usa su propio contenedor).
-->
<style lang="scss">

.pdf-field-editor__overlay .moveable-line {
  background: #ff6a00 !important;
}

.pdf-field-editor__overlay .moveable-control {
  width: 14px !important;
  height: 14px !important;
  margin-top: -7px !important;
  margin-left: -7px !important;
  background: #ff6a00 !important;
  border: 2px solid #fff !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5) !important;
  box-sizing: border-box !important;
}

.pdf-field-editor__overlay .moveable-control:hover {
  background: #d85600 !important;
}

</style>
